import { ref, watch, computed } from 'vue';

export default function useSvgViewBox({
  svg, containerWidth, containerHeight, eventmap,
}) {
  const viewBox = ref([0, 0, 0, 0]); // Reactive viewBox array [x, y, width, height]
  let initW = false; // Flag to track initial width setup
  let initH = false; // Flag to track initial height setup

  const zoomFactor = computed(() => {
    if (!eventmap?.width || !viewBox.value[2]) return 1;
    return eventmap.width / viewBox.value[2]; // Or use initialViewBox width if stored separately
  });

  const updateViewBox = (targetX, targetY, targetWidth, targetHeight, easeFactor) => {
    viewBox.value = [
      viewBox.value[0] + (targetX - viewBox.value[0]) * easeFactor,
      viewBox.value[1] + (targetY - viewBox.value[1]) * easeFactor,
      viewBox.value[2] + (targetWidth - viewBox.value[2]) * easeFactor,
      viewBox.value[3] + (targetHeight - viewBox.value[3]) * easeFactor,
    ];
  };

  const easeInOut = (progress) => 0.5 - Math.cos(progress * Math.PI) / 2;

  const animateTransition = (targetX, targetY, targetWidth, targetHeight, duration = 1000) => {
    const startTime = performance.now();
    function step(time) {
      let progress = (time - startTime) / duration;
      progress = Math.min(1, progress);
      const easeFactor = easeInOut(progress);
      updateViewBox(targetX, targetY, targetWidth, targetHeight, easeFactor);
      if (progress < 1) requestAnimationFrame(step);
    }
    requestAnimationFrame(step);
  };

  const resetViewBox = (animate = true) => {
    const multiplier = (containerWidth.value / containerHeight.value) * (eventmap.depth / eventmap.width);
    const viewportHeight = (1.1 * eventmap.depth) / Math.min(1, multiplier);
    const viewportWidth = 1.1 * eventmap.width * Math.max(1, multiplier);
    const startX = (viewportWidth - eventmap.width) / 2;
    const startY = (viewportHeight - eventmap.depth) / 2;

    if (animate) {
      animateTransition(-startX, -startY, viewportWidth, viewportHeight);
    } else {
      viewBox.value = [-startX, -startY, viewportWidth, viewportHeight];
    }
  };

  const screenToSvgCoordinates = (x, y) => {
    const ctm = svg.value.getScreenCTM();
    if (!ctm) return { x: 0, y: 0 };
    return DOMPoint.fromPoint({ x, y }).matrixTransform(ctm.inverse());
  };

  const zoomToPoint = (scaleFactor, x, y) => {
    const svgPoint = screenToSvgCoordinates(x, y);
    viewBox.value = [
      (viewBox.value[0] - svgPoint.x) * scaleFactor + svgPoint.x,
      (viewBox.value[1] - svgPoint.y) * scaleFactor + svgPoint.y,
      Math.max(viewBox.value[2] * scaleFactor, 1),
      Math.max(viewBox.value[3] * scaleFactor, 1),
    ];
  };

  const zoom = (scaleFactor) => {
    const newWidth = viewBox.value[2] * scaleFactor;
    const newHeight = viewBox.value[3] * scaleFactor;
    const newX = viewBox.value[0] + ((viewBox.value[2] - newWidth) / 2);
    const newY = viewBox.value[1] + ((viewBox.value[3] - newHeight) / 2);
    animateTransition(newX, newY, newWidth, newHeight, 500);
  };

  const pan = (x, y) => {
    const ctm = svg.value.getScreenCTM();
    const panned = new DOMMatrix([0, 0, x, y, 0, 0]).multiply(ctm.inverse());
    viewBox.value[0] += panned.c - panned.a;
    viewBox.value[1] += panned.d - panned.b;
  };

  const zoomToLocation = (location) => {
    const scaleX = containerWidth.value / location.width;
    const scaleY = containerHeight.value / location.depth;
    const scale = Math.min(scaleX, scaleY);
    const viewBoxWidth = (containerWidth.value / scale) * 2;
    const viewBoxHeight = (containerHeight.value / scale) * 2;
    const viewBoxX = location.centre_x - (viewBoxWidth / 2);
    const viewBoxY = location.centre_y - (viewBoxHeight / 2);
    const clampedViewBoxX = Math.max(0, Math.min(viewBoxX, eventmap.width - viewBoxWidth));
    const clampedViewBoxY = Math.max(0, Math.min(viewBoxY, eventmap.depth - viewBoxHeight));
    animateTransition(clampedViewBoxX, clampedViewBoxY, viewBoxWidth, viewBoxHeight);
  };

  const zoomToExtents = (x1, y1, x2, y2, factor = 1.9) => {
    let newWidth; let
      newHeight;
    const minX = Math.min(x1, x2);
    const maxX = Math.max(x1, x2);
    const minY = Math.min(y1, y2);
    const maxY = Math.max(y1, y2);
    const multiplier = containerWidth.value / containerHeight.value;

    if (!Number.isFinite(multiplier) || multiplier === 0) return;

    if ((maxX - minX) > (maxY - minY)) {
      newWidth = (maxX - minX) * factor;
      newHeight = newWidth / multiplier;
    } else {
      newHeight = (maxY - minY) * factor;
      newWidth = newHeight * multiplier;
    }

    const centreX = minX + ((maxX - minX) / 2);
    const centreY = minY + ((maxY - minY) / 2);

    animateTransition(
      centreX - newWidth / 2,
      centreY - newHeight / 2,
      newWidth,
      newHeight,
    );
  };

  // Watch for changes in container dimensions and adjust the viewBox accordingly
  watch([containerWidth, containerHeight], ([newWidth, newHeight], [oldWidth, oldHeight]) => {
    if (!initW && !initH) {
      initW = true;
      initH = true;
      return;
    }
    if (newWidth !== oldWidth && oldWidth) {
      viewBox.value[2] *= Math.max(newWidth, 1) / Math.max(oldWidth, 1);
    }
    if (newHeight !== oldHeight && oldHeight) {
      viewBox.value[3] *= Math.max(newHeight, 1) / Math.max(oldHeight, 1);
    }
  });

  return {
    viewBox,
    zoomFactor,
    animateTransition,
    resetViewBox,
    zoomToPoint,
    zoom,
    pan,
    zoomToLocation,
    zoomToExtents,
    screenToSvgCoordinates,
  };
}
